/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  DisplayText,
  Heading
} from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { ContractIncludedItem } from '@nile/types';
import envConfig from '@nile/home/src/Config/env';
import {
  CurrencyText,
  DisabledTag,
  SuperTable,
  CurrencyField,
  getTranslatedText
} from '@nile/components';
import { isEmpty } from 'lodash';

function DynamicCell({ cell }: { cell: any }) {
  const { row, column } = cell;
  const { onChange } = column.actions;

  const intl = useIntl();

  const onChaneHandler = (accessor: string, value: number) => {
    onChange({
      updatedItem: {
        ...row.original,
        sellingPrice: {
          ...row.original?.sellingPrice,
          model: 'PER_UNIT',
          details: [
            {
              ...row.original.sellingPrice?.details?.[0],
              [accessor]: isEmpty(value) ? null : value?.toString()
            }
          ]
        }
      },
      original: row.original
    });
  };

  switch (cell.column.accessor) {
    case 'currentAfterHours':
      if (
        row.original.serviceCatalogItem?.serviceTypeItem?.offeringType ===
        'HOURS'
      ) {
        return (
          <CurrencyText
            value={
              row.original.serviceCatalogItem?.sellingPrice?.details?.[0]
                ?.afterHoursValue
            }
          />
        );
      }
      return (
        <DisabledTag>
          <FormattedMessage id="app.noData.message" />
        </DisabledTag>
      );
    case 'currentUnitPrice':
      if (
        ['QUANTITY', 'BLOCK_HOUR', 'BLOCK_MONEY'].includes(
          row.original.serviceCatalogItem?.serviceTypeItem?.offeringType
        ) ||
        row.original.serviceCatalogItem?.itemType === 'PRODUCT'
      ) {
        return (
          <CurrencyText
            value={
              row.original.serviceCatalogItem?.serviceTypeItem?.offeringType ===
              'BLOCK_MONEY'
                ? row.original.serviceCatalogItem?.serviceTypeItem
                    ?.redeemableServiceBundle?.redeemableQuantity
                : row.original.serviceCatalogItem.sellingPrice.details[0].value
            }
          />
        );
      }
      return (
        <DisabledTag>
          <FormattedMessage id="app.noData.message" />
        </DisabledTag>
      );
    case 'currentBusinessHours':
      if (
        row.original.serviceCatalogItem?.serviceTypeItem?.offeringType ===
        'HOURS'
      ) {
        return (
          <CurrencyText
            value={
              row.original.serviceCatalogItem?.sellingPrice?.details?.[0]?.value
            }
          />
        );
      }
      return (
        <DisabledTag>
          <FormattedMessage id="app.noData.message" />
        </DisabledTag>
      );
    case 'modifyUnitPrice':
      if (
        ['QUANTITY', 'BLOCK_HOUR', 'BLOCK_MONEY'].includes(
          row.original.serviceCatalogItem?.serviceTypeItem?.offeringType
        ) ||
        row.original.serviceCatalogItem?.itemType === 'PRODUCT'
      ) {
        return (
          <span className="fw no-scroll">
            <CurrencyField
              showCurrency
              value={row.original.sellingPrice?.details?.[0]?.value?.toString()}
              autoComplete="off"
              onChange={(value) => onChaneHandler('value', value)}
              type="number"
              min={0}
              step={1}
              size="extrasmall"
            />
          </span>
        );
      }
      return (
        <DisabledTag>
          <FormattedMessage id="app.noData.message" />
        </DisabledTag>
      );
    case 'modifyBusinessHours':
    case 'modifyAfterHours':
      if (
        row.original.serviceCatalogItem?.serviceTypeItem?.offeringType ===
        'HOURS'
      ) {
        const accessor =
          cell.column.accessor === 'modifyBusinessHours'
            ? 'value'
            : 'afterHoursValue';
        let errorMessage = null;
        if (accessor === 'value') {
          if (
            row.original.sellingPrice?.details?.[0]?.afterHoursValue &&
            !row.original.sellingPrice?.details?.[0]?.[accessor]
          ) {
            errorMessage = getTranslatedText(
              intl,
              'value.validation.description.empty'
            );
          }
        }
        return (
          <div className="fw no-scroll">
            <CurrencyField
              showCurrency
              value={row.original.sellingPrice?.details?.[0]?.[
                accessor
              ]?.toString()}
              autoComplete="off"
              size="extrasmall"
              onChange={(value) => onChaneHandler(accessor, value)}
              type="number"
              min={0}
              step={1}
              error={errorMessage}
            />
          </div>
        );
      }
      return (
        <DisabledTag>
          <FormattedMessage id="app.noData.message" />
        </DisabledTag>
      );
    default:
      return <div>{cell.value}</div>;
  }
}

export const ModifyUnitPricePopup = ({
  title,
  selectedItems,
  setSelectedItems,
  closeModal
}: {
  title?: string;
  selectedItems?: ContractIncludedItem[];
  setSelectedItems?: (items: ContractIncludedItem[]) => void;
  closeModal: () => void;
}) => {
  const intl = useIntl();

  const [records, setRecords] = useState<ContractIncludedItem[]>(
    selectedItems || []
  );

  const [errors, setErrors] = useState<boolean>(false);

  const [columnGroup, setColumnGroup] = useState<any[]>([]);

  const columns = [
    {
      Header: getTranslatedText(intl, 'admin.product.and.services.title'),
      accessor: 'serviceCatalogItem.name'
    },
    {
      Header: getTranslatedText(
        intl,
        'client.contract.modifyUnitPrice.header.current.unitPrice'
      ),
      accessor: 'currentUnitPrice'
    },
    {
      Header: getTranslatedText(
        intl,
        'client.contract.modifyUnitPrice.header.modify.unitPrice'
      ),
      accessor: 'modifyUnitPrice'
    },
    {
      Header: getTranslatedText(
        intl,
        'client.contract.modifyUnitPrice.header.current.businessHours'
      ),
      accessor: 'currentBusinessHours'
    },
    {
      Header: getTranslatedText(
        intl,
        'client.contract.modifyUnitPrice.header.modify.businessHours'
      ),
      accessor: 'modifyBusinessHours'
    },
    {
      Header: getTranslatedText(
        intl,
        'client.contract.modifyUnitPrice.header.current.afterHours'
      ),
      accessor: 'currentAfterHours'
    },
    {
      Header: getTranslatedText(
        intl,
        'client.contract.modifyUnitPrice.header.modify.afterHours'
      ),
      accessor: 'modifyAfterHours'
    }
  ];

  const defaultColumnCells = {
    DynamicCell: DynamicCell
  };

  function generateColumns() {
    const cols = columns.map((column) => {
      return {
        ...column,
        id: column.accessor,
        accessorKey: column.accessor,
        accessor: column.accessor,
        name: column.Header,
        header: column.Header,
        Header: column.Header,
        type: 'DynamicCell',
        width: 150,
        size: 150,
        disableResizing: false,
        disableSortBy: true,
        disableFilters: true,
        field: {
          name: column.accessor
        },
        actions: {
          onChange: ({
            updatedItem,
            original
          }: {
            updatedItem: ContractIncludedItem;
            original: ContractIncludedItem;
          }) => {
            setRecords((prev) => {
              return prev.map((item) => {
                if (
                  item.serviceCatalogItem.itemId ===
                  original.serviceCatalogItem.itemId
                ) {
                  return updatedItem;
                }
                return item;
              });
            });
          }
        }
      };
    });
    setColumnGroup([{ Header: 'Name', header: 'Name', columns: cols }]);
  }

  useEffect(() => {
    generateColumns();
  }, []);

  function checkValidForm() {
    const invalidItems = records.some((item) => {
      return (
        item.sellingPrice?.details?.[0]?.afterHoursValue &&
        !item.sellingPrice?.details?.[0]?.value
      );
    });
    if (invalidItems) {
      setErrors(true);
      return false;
    }
    return true;
  }

  function handleSave() {
    if (!checkValidForm()) {
      return;
    }
    setErrors(false);
    if (setSelectedItems) {
      const cleanedRecords = records.map((item) => {
        return {
          ...item,
          sellingPrice:
            !item.sellingPrice?.details?.[0]?.afterHoursValue &&
            !item.sellingPrice?.details?.[0]?.value
              ? null
              : item.sellingPrice
        };
      });
      setSelectedItems(cleanedRecords);
    }
    closeModal();
  }

  return (
    <div className="w-80vw vh80 bg-white br10">
      <div className="fx actr fw p20 divider space-between">
        <Heading element="h1" weight="Bold">
          <FormattedMessage id={title || 'admin.serviceCatalog.header'} />
        </Heading>
        <ButtonGroup>
          <Button onClick={closeModal} plainSecondary>
            <FormattedMessage id="common.cancel" />
          </Button>
          <Button primary icon={<SVGIcon name="Save" />} onClick={handleSave}>
            <FormattedMessage id="common.save" />
          </Button>
        </ButtonGroup>
      </div>

      <SuperTable
        data={records}
        padded="sm"
        context={{
          records: [],
          viewConditions: null
        }}
        setData={setRecords}
        rowCount={records?.length}
        refreshTable={generateColumns}
        columns={columnGroup}
        defaultColumnCells={defaultColumnCells}
        emptyState={{
          nodataImage: `${envConfig.imageRoot}svg/Invoice.svg`,
          heading: getTranslatedText(
            intl,
            'admin.mandatoryTicketFields.list.noData.heading'
          ),
          description: getTranslatedText(
            intl,
            'admin.mandatoryTicketFields.list.noData.description'
          )
        }}
        listInfo={{
          totalCount: records?.length
        }}
        noBulkSelect
        noOverscan
      />
    </div>
  );
};
